import { Controller } from "@hotwired/stimulus"

// Generic toggle for create-or-use-existing pickers (Amazon DSP Conversions, Amazon ADM
// Audiences, Microsoft Customer Match, ...). Reveals one of two field branches based on the
// selected radio's value, and `disabled`s the inactive branch's inputs so they're excluded
// from form serialization (a CSS-hidden but enabled input would otherwise overwrite the
// active branch's value on submit).
//
// Values:
//   - default-mode: fallback if no radio is currently checked
//   - new-mode:     radio value that maps to the "new" branch (newField target)
//   - existing-mode: radio value that maps to the "existing" branch (existingField target)
//   - radio-name:   substring matched against the radio inputs' `name` attribute. Defaults to
//                   "dataset_mode" for backwards compatibility with existing Amazon usages.
//                   Set to e.g. "audience_mode" when the field is named differently.
//
// Usage example (Amazon ADM Audiences):
//   <div data-controller="dataset-mode-toggle"
//        data-dataset-mode-toggle-default-mode-value="create_new"
//        data-dataset-mode-toggle-new-mode-value="create_new"
//        data-dataset-mode-toggle-existing-mode-value="use_existing">
//     <input type="radio" name="...[dataset_mode]" value="create_new"
//            data-action="change->dataset-mode-toggle#toggle">
//     ...
//     <div data-dataset-mode-toggle-target="newField">...</div>
//     <div data-dataset-mode-toggle-target="existingField">...</div>
//   </div>
export default class extends Controller {
  static targets = ["newField", "existingField"]
  static values = {
    defaultMode: { type: String, default: "" },
    newMode: { type: String, default: "" },
    existingMode: { type: String, default: "" },
    radioName: { type: String, default: "dataset_mode" }
  }

  connect() {
    this.applyMode()
  }

  toggle(event) {
    this.applyMode(event.target.value)
  }

  applyMode(mode) {
    const selector = `input[type=radio][name*="${this.radioNameValue}"]:checked`
    const selected = mode || this.element.querySelector(selector)?.value || this.defaultModeValue

    this.toggleFieldset(this.hasNewFieldTarget ? this.newFieldTarget : null, selected === this.newModeValue)
    this.toggleFieldset(this.hasExistingFieldTarget ? this.existingFieldTarget : null, selected === this.existingModeValue)
  }

  toggleFieldset(el, active) {
    if (!el) return
    el.classList.toggle("hidden", !active)
    el.querySelectorAll("input, select, textarea").forEach((field) => {
      field.disabled = !active
    })
  }
}
